import { getStoredToken } from '@/services/authScope';

export default function access(
  initialState:
    | {
        currentUser?: API.CurrentUser;
        currentSubAccount?: API.SubAccountProfile;
        currentAuthScope?: 'admin' | 'sub' | 'public';
      }
    | undefined,
) {
  const { currentUser, currentSubAccount, currentAuthScope } =
    initialState ?? {};
  const permissionSet = new Set(currentSubAccount?.permissions ?? []);
  const hasAdminToken = !!getStoredToken('admin');
  const hasSubToken = !!getStoredToken('sub');
  const isAdminScope = currentAuthScope === 'admin';
  const isSubScope = currentAuthScope === 'sub';
  const hasAdminSession = isAdminScope && hasAdminToken && !!currentUser;
  const hasSubSession = isSubScope && hasSubToken && !!currentSubAccount;
  const isAdminBootstrapPending = isAdminScope && hasAdminToken && !currentUser;
  const isSubBootstrapPending = isSubScope && hasSubToken && !currentSubAccount;

  const hasSubPermission = (permission: API.PermissionCode): boolean =>
    permissionSet.has(permission);

  const adminAbilitiesByRole: Record<API.Admin['role'], string[]> = {
    super_admin: ['*'],
    admin: [
      'dashboard:read',
      'accounts:read',
      'accounts:write',
      'proxies:read',
      'proxies:write',
      'paypal_apps:read',
      'webhooks:read',
      'webhooks:write',
      'logs:read',
      'oauth:read',
      'oauth:write',
      'reviews:read',
      'reviews:write',
      'settlements:read',
      'settlements:write',
      'sub_accounts:read',
      'sub_accounts:write',
      'orders:read',
      'orders:write',
      'receiving:read',
      'receiving:write',
    ],
    viewer: [
      'dashboard:read',
      'accounts:read',
      'proxies:read',
      'paypal_apps:read',
      'webhooks:read',
      'logs:read',
      'oauth:read',
      'reviews:read',
      'settlements:read',
      'sub_accounts:read',
      'orders:read',
      'receiving:read',
    ],
  };

  const hasAdminAbility = (ability: string): boolean => {
    if (isAdminBootstrapPending) {
      return true;
    }

    if (!hasAdminSession || !currentUser) {
      return false;
    }

    const abilities = currentUser.abilities?.length
      ? currentUser.abilities
      : (adminAbilitiesByRole[currentUser.role] ?? []);

    return abilities.includes('*') || abilities.includes(ability);
  };

  return {
    canAdmin:
      ((currentUser?.role === 'super_admin' || currentUser?.role === 'admin') &&
        hasAdminSession) ||
      isAdminBootstrapPending,
    canSuperAdmin: currentUser?.role === 'super_admin' && hasAdminSession,
    canAdminView: hasAdminSession || isAdminBootstrapPending,
    canView:
      hasAdminSession ||
      hasSubSession ||
      isAdminBootstrapPending ||
      isSubBootstrapPending,
    canSubView: hasSubSession || isSubBootstrapPending,

    canDashboardRead: hasAdminAbility('dashboard:read'),
    canAccountsRead: hasAdminAbility('accounts:read'),
    canAccountsWrite: hasAdminAbility('accounts:write'),
    canProxiesRead: hasAdminAbility('proxies:read'),
    canProxiesWrite: hasAdminAbility('proxies:write'),
    canPaypalAppsRead: hasAdminAbility('paypal_apps:read'),
    canPaypalAppsWrite: hasAdminAbility('paypal_apps:write'),
    canWebhooksRead: hasAdminAbility('webhooks:read'),
    canWebhooksWrite: hasAdminAbility('webhooks:write'),
    canLogsRead: hasAdminAbility('logs:read'),
    canOauthRead: hasAdminAbility('oauth:read'),
    canOauthWrite: hasAdminAbility('oauth:write'),
    canReviewsRead: hasAdminAbility('reviews:read'),
    canReviewsWrite: hasAdminAbility('reviews:write'),
    canSettlementsRead: hasAdminAbility('settlements:read'),
    canSettlementsWrite: hasAdminAbility('settlements:write'),
    canSubAccountsRead: hasAdminAbility('sub_accounts:read'),
    canSubAccountsWrite: hasAdminAbility('sub_accounts:write'),
    canOrdersRead: hasAdminAbility('orders:read'),
    canOrdersWrite: hasAdminAbility('orders:write'),
    canReceivingRead: hasAdminAbility('receiving:read'),
    canReceivingWrite: hasAdminAbility('receiving:write'),
    canSubAccountsView:
      (hasSubSession && hasSubPermission('sub.accounts.view')) ||
      isSubBootstrapPending,
    canSubOrdersCreate:
      (hasSubSession && hasSubPermission('sub.orders.create')) ||
      isSubBootstrapPending,
    canSubOrdersView:
      (hasSubSession && hasSubPermission('sub.orders.view')) ||
      isSubBootstrapPending,
    canSubOrdersCancel:
      (hasSubSession && hasSubPermission('sub.orders.cancel')) ||
      isSubBootstrapPending,
    canSubSettlementsCreate:
      (hasSubSession && hasSubPermission('sub.settlements.create')) ||
      isSubBootstrapPending,
    canSubSettlementsView:
      (hasSubSession && hasSubPermission('sub.settlements.view')) ||
      isSubBootstrapPending,
    canSubOrdersAccess:
      (hasSubSession &&
        (hasSubPermission('sub.orders.view') ||
          hasSubPermission('sub.orders.create') ||
          hasSubPermission('sub.orders.cancel'))) ||
      isSubBootstrapPending,
    canSubSettlementsAccess:
      (hasSubSession &&
        (hasSubPermission('sub.settlements.view') ||
          hasSubPermission('sub.settlements.create'))) ||
      isSubBootstrapPending,
  };
}
